/**
 * CreatureCard Component
 *
 * Displays a creature card in the gallery grid with:
 * - Creature sprite image
 * - Creature name (with tooltip on truncation)
 * - Primary combat abilities (top 2-3)
 * - Click handler to view full details
 * - Share to Discord button (for owners only)
 */

import React from 'react';
import type { SerializedCreatureDocument } from '@shared/types/creature-storage';
import { Tooltip } from './Tooltip';
import { useIsOverflowing } from '../hooks/useIsOverflowing';
import { ShareToDiscordButton } from './ShareToDiscordButton';
import { DeleteCreatureButton } from './DeleteCreatureButton';
import './CreatureCard.css';

export interface CreatureCardProps {
  creature: SerializedCreatureDocument;
  onClick?: (creature: SerializedCreatureDocument) => void;
  /** Current user ID for owner check - shows share/delete buttons if matches creature owner */
  currentUserId?: string;
  /** Callback fired when share completes (success or failure) */
  onShareComplete?: (success: boolean, message: string) => void;
  /** Callback fired when delete completes (success or failure) */
  onDeleteComplete?: (success: boolean, message: string, creatureId: string) => void;
}

export const CreatureCard: React.FC<CreatureCardProps> = ({
  creature,
  onClick,
  currentUserId,
  onShareComplete,
  onDeleteComplete,
}) => {
  // Track if the creature name is truncated
  const [nameRef, isNameTruncated] = useIsOverflowing<HTMLHeadingElement>();

  // Check if current user is the owner of this creature
  const isOwner = currentUserId && creature.ownerId === currentUserId;

  const handleClick = () => {
    onClick?.(creature);
  };

  // Prevent action button clicks from triggering card navigation
  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  // Handle delete completion - wrap to include creature ID
  const handleDeleteComplete = (success: boolean, message: string) => {
    onDeleteComplete?.(success, message, creature.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(creature);
    }
  };

  // Get the menu sprite (main display sprite)
  const spriteUrl = creature.sprites.menuSprite;

  // Get top 3 combat attributes for display
  const topAbilities = creature.combatAttributes.slice(0, 3);

  // Format creation date
  const createdDate = new Date(creature.createdAt).toLocaleDateString();

  return (
    <div
      className="creature-card"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${creature.name}`}
    >
      {/* Sprite Image */}
      <div className="creature-card__image-container">
        <img
          src={spriteUrl}
          alt={creature.name}
          className="creature-card__image"
          loading="lazy"
          onError={(e) => {
            // Fallback for failed image loads
            const target = e.target as HTMLImageElement;
            target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64"%3E%3Crect width="64" height="64" fill="%23ccc"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23666" font-size="12"%3E?%3C/text%3E%3C/svg%3E';
          }}
        />
        {/* Action buttons - only visible for creature owners */}
        {isOwner && (
          <div className="creature-card__actions" onClick={handleActionClick}>
            <ShareToDiscordButton
              creatureId={creature.id}
              onShareComplete={onShareComplete}
            />
            <DeleteCreatureButton
              creatureId={creature.id}
              creatureName={creature.name}
              onDeleteComplete={handleDeleteComplete}
            />
          </div>
        )}
      </div>

      {/* Creature Info */}
      <div className="creature-card__content">
        <Tooltip content={creature.name} enabled={isNameTruncated} multiline>
          <h3 ref={nameRef} className="creature-card__name">
            {creature.name}
          </h3>
        </Tooltip>

        {/* Combat Attributes */}
        {topAbilities.length > 0 && (
          <div className="creature-card__abilities">
            {topAbilities.map((attr) => (
              <span
                key={attr.attributeId}
                className="creature-card__ability"
                title={`${attr.category} - ${attr.attackType}`}
              >
                {attr.name}
              </span>
            ))}
          </div>
        )}

        {/* Metadata */}
        <div className="creature-card__metadata">
          <span className="creature-card__race">{creature.race}</span>
          <span className="creature-card__class">{creature.class}</span>
          <span className="creature-card__date">{createdDate}</span>
        </div>
      </div>
    </div>
  );
};
